import { fn } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import LineClamp from 'garaje/components/line-clamp';
import removedString from 'garaje/helpers/removed-string';
import type Entry from 'garaje/models/entry';
import italicizeIfRemoved from 'garaje/modifiers/italicize-if-removed';
import { isSensitiveFieldMasked, type SensitiveState } from 'garaje/services/sensitive-fields-visibility';
import type SensitiveFieldsVisibilityService from 'garaje/services/sensitive-fields-visibility';

interface HostColumnSignature {
  Args: {
    entry: Entry;
  };
  Element: HTMLDivElement;
}

const HOST_FIELD = 'Host';

export default class HostColumn extends Component<HostColumnSignature> {
  @service declare sensitiveFieldsVisibility: SensitiveFieldsVisibilityService;
  @tracked nameWasTruncated = false;

  get sensitiveState(): SensitiveState | undefined {
    return this.args.entry.sensitiveFields?.[HOST_FIELD];
  }

  get isSensitiveMasked(): boolean {
    return isSensitiveFieldMasked(this.sensitiveState, this.sensitiveFieldsVisibility.isRevealed(HOST_FIELD));
  }

  get host(): string {
    if (this.isSensitiveMasked) {
      return 'Hidden';
    }

    const { entry } = this.args;
    const userData = entry.userData;
    const matchingField = userData.findBy('field', HOST_FIELD);

    return matchingField?.value ?? entry.host ?? '';
  }

  <template>
    <div data-private data-test-host-column class="table-cell border-b align-top p-2 text-carbon-50" ...attributes>
      <div class="h-12 flex items-center">
        <span data-test-host-column class={{if this.isSensitiveMasked "italic"}}>
          <LineClamp
            @useNative={{true}}
            @handleTruncate={{fn (mut this.nameWasTruncated)}}
            @lines={{2}}
            @lineHeight={{1.25}}
            @text={{removedString this.host}}
            {{italicizeIfRemoved this.host}}
          />
        </span>

        {{#if this.nameWasTruncated}}
          <EmberPopover
            @tooltipClass="break-word max-w-sm"
            @popperContainer=".ui__page"
            @delay={{400}}
            @side="top"
            @text={{removedString this.host}}
          />
        {{/if}}
      </div>
    </div>
  </template>
}
